// Small by design — the theme is CSS-first. This wires the mobile menu,
// scroll reveals, the header shadow, and the cart-badge pop. Everything
// degrades to fully-visible static content without JS, and all motion is
// skipped when the visitor prefers reduced motion.
document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

document.querySelectorAll('[data-menu-toggle]').forEach((toggle) => {
  toggle.addEventListener('click', () => {
    const nav = document.getElementById('SiteNav');
    const open = nav.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(open));
  });
});

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Page continuity — a quick botanical field closes over the current page while
// an internal destination opens. Returning to a browser tab gets the same brief
// acknowledgement, without inventing network progress or delaying reduced-
// motion visitors. Content stays visible unless JavaScript explicitly starts it.
const pageTransition = document.querySelector('[data-page-transition]');
if (pageTransition && !reducedMotion && !window.Shopify?.designMode) {
  const transitionLabel = pageTransition.querySelector('[data-page-transition-label]');
  let transitionTimer;
  let isLeaving = false;
  let hiddenAt = 0;

  const clearTransition = () => {
    window.clearTimeout(transitionTimer);
    document.documentElement.classList.remove('is-page-transitioning', 'is-page-transition-out');
    isLeaving = false;
  };

  const startTransition = (label, { exit = false } = {}) => {
    window.clearTimeout(transitionTimer);
    if (transitionLabel) transitionLabel.textContent = label;
    document.documentElement.classList.remove('is-page-transition-out');
    document.documentElement.classList.add('is-page-transitioning');

    if (exit) {
      transitionTimer = window.setTimeout(() => {
        document.documentElement.classList.add('is-page-transition-out');
        transitionTimer = window.setTimeout(clearTransition, 300);
      }, 420);
    }
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || link.target === '_blank' || link.dataset.noPageTransition !== undefined) return;

    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin || !['http:', 'https:'].includes(destination.protocol)) return;
    const sameDocument = destination.pathname === window.location.pathname && destination.search === window.location.search;
    if (sameDocument && destination.hash) return;
    if (destination.href === window.location.href) return;

    event.preventDefault();
    isLeaving = true;
    startTransition('Opening the greenhouse');
    window.setTimeout(() => window.location.assign(destination.href), 480);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }
    if (!isLeaving && hiddenAt && Date.now() - hiddenAt > 600) {
      startTransition('Back in the greenhouse', { exit: true });
    }
    hiddenAt = 0;
  });

  window.addEventListener('pageshow', clearTransition);
}

// Scroll reveals: tag the interesting building blocks, then flip them visible
// as they enter the viewport, staggered a touch within their parent so grids
// ripple instead of popping in as one slab.
const revealTargets = document.querySelectorAll(
  [
    '.split-hero__copy > *',
    '.product-grid > li',
    '.reviews__grid > li',
    '.record-field',
    '.care-topics > li',
    '.guide-grid > li',
    '.genus-grid > li',
    '.trust-grid > li',
    '.ig-wall__grid > li',
    '.socials__grid > li',
    '.ll-moments > li',
    '.ll-bill > li',
    '.app-promo__features > li',
    '.featured-guide',
    '.feature-row',
    '.video-feature > *',
    '.search-empty',
  ].join(', '),
);
if (!reducedMotion && 'IntersectionObserver' in window) {
  const byParent = new Map();
  revealTargets.forEach((el) => {
    el.setAttribute('data-reveal', '');
    const siblings = byParent.get(el.parentElement) ?? 0;
    el.style.setProperty('--reveal-delay', `${Math.min(siblings * 70, 350)}ms`);
    byParent.set(el.parentElement, siblings + 1);
  });
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
  );
  revealTargets.forEach((el) => observer.observe(el));
}

// Header gains depth once the page starts moving under it.
const header = document.querySelector('.site-header');
if (header) {
  const setScrolled = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
  setScrolled();
  window.addEventListener('scroll', setScrolled, { passive: true });
}

// Cart badge pop — greets a non-empty cart on arrival (covers the classic
// add-to-cart round trip, which lands back here with the count updated).
const cartCount = document.querySelector('.cart-count');
if (cartCount && !reducedMotion && cartCount.textContent.trim() !== '0') {
  requestAnimationFrame(() => cartCount.classList.add('did-pop'));
}

// Inline predictive search — Shopify's suggest endpoint, rendered in a
// dropdown so the shopper never leaves the page they're browsing.
const searchToggle = document.querySelector('[data-search-toggle]');
const searchPanel = document.getElementById('HeaderSearch');
const searchInput = document.querySelector('[data-search-input]');
const searchResults = document.querySelector('[data-search-results]');
if (searchToggle && searchPanel && searchInput && searchResults) {
  const searchClose = searchPanel.querySelector('[data-search-close]');
  const closeSearch = () => {
    searchPanel.classList.remove('is-open');
    searchToggle.setAttribute('aria-expanded', 'false');
    searchToggle.focus();
  };
  searchToggle.addEventListener('click', () => {
    const open = searchPanel.classList.toggle('is-open');
    searchToggle.setAttribute('aria-expanded', String(open));
    if (open) searchInput.focus();
  });
  searchClose?.addEventListener('click', closeSearch);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && searchPanel.classList.contains('is-open')) {
      closeSearch();
    }
  });

  const escapeHtml = (value) =>
    value.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);

  let debounceTimer;
  let lastQuery = '';
  searchInput.addEventListener('input', () => {
    const query = searchInput.value.trim();
    clearTimeout(debounceTimer);
    if (query.length < 2) {
      searchResults.classList.remove('has-results');
      searchResults.innerHTML = '';
      lastQuery = '';
      return;
    }
    debounceTimer = setTimeout(async () => {
      if (query === lastQuery) return;
      lastQuery = query;
      try {
        const response = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article&resources[limit]=6&resources[options][unavailable_products]=last`,
        );
        const json = await response.json();
        const products = json?.resources?.results?.products ?? [];
        const collections = json?.resources?.results?.collections ?? [];
        const articles = json?.resources?.results?.articles ?? [];
        if (searchInput.value.trim() !== query) return; // stale response
        const productRows = products
          .map((product) => `
            <li class="search-results__item"><a href="${product.url}">
              ${product.image ? `<img src="${product.image}${product.image.includes('?') ? '&' : '?'}width=88" alt="" loading="lazy">` : '<span class="sr-placeholder"></span>'}
              <span class="sr-copy"><span class="sr-title">${escapeHtml(product.title)}</span><span class="sr-kind">Plant</span></span>
              <span class="sr-price">${product.price ? `$${product.price}` : ''}</span>
            </a></li>`)
          .join('');
        const discoveryRows = [...collections.map((item) => ({ ...item, kind: 'Collection' })), ...articles.map((item) => ({ ...item, kind: 'Care guide' }))]
          .slice(0, 5)
          .map((item) => `<li class="search-results__item search-results__item--text"><a href="${item.url}"><span class="sr-copy"><span class="sr-title">${escapeHtml(item.title)}</span><span class="sr-kind">${item.kind}</span></span><span aria-hidden="true">&#8594;</span></a></li>`)
          .join('');
        const productGroup = productRows ? `<li class="search-results__label">Plants</li>${productRows}` : '';
        const discoveryGroup = discoveryRows ? `<li class="search-results__label">Explore and learn</li>${discoveryRows}` : '';
        searchResults.innerHTML = `${productGroup}${discoveryGroup}<li><a class="sr-all" href="/search?q=${encodeURIComponent(query)}&type=product">${products.length ? 'See every plant result' : 'No plant matches yet — search the full greenhouse'} for “${escapeHtml(query)}”</a></li>`;
        searchResults.classList.add('has-results');
      } catch {
        searchResults.classList.remove('has-results');
      }
    }, 180);
  });
}

// Autoplay storytelling videos while they are actually visible. Muting keeps
// browser autoplay reliable; the local control and reduced-motion preference
// always give the visitor the final say.
const initializeAutoplayVideos = (scope = document) => {
  scope.querySelectorAll('[data-autoplay-video]:not([data-video-ready])').forEach((root) => {
    const media = root.querySelector('video, iframe');
    const toggle = root.querySelector('[data-video-toggle]');
    if (!media || !toggle) return;

    root.setAttribute('data-video-ready', '');
    const provider = media.dataset.videoProvider || 'shopify';
    const pauseIcon = toggle.querySelector('[data-video-pause-icon]');
    const playIcon = toggle.querySelector('[data-video-play-icon]');
    const label = toggle.querySelector('[data-video-toggle-label]');
    let inView = false;
    let visitorPaused = reducedMotion;

    const updateControl = (playing) => {
      const action = playing ? 'Pause video' : 'Play video';
      toggle.setAttribute('aria-label', action);
      label.textContent = action;
      pauseIcon.hidden = !playing;
      playIcon.hidden = playing;
    };

    const command = (playing) => {
      updateControl(playing);
      if (provider === 'shopify') {
        media.muted = true;
        media.loop = true;
        media.playsInline = true;
        if (playing) {
          const playback = media.play();
          if (playback) playback.catch(() => updateControl(false));
        } else {
          media.pause();
        }
        return;
      }

      if (!media.contentWindow) return;
      if (provider === 'youtube') {
        media.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: playing ? 'playVideo' : 'pauseVideo', args: [] }),
          '*',
        );
      } else if (provider === 'vimeo') {
        media.contentWindow.postMessage(JSON.stringify({ method: playing ? 'play' : 'pause' }), '*');
      }
    };

    const syncPlayback = () => command(!visitorPaused && inView && !document.hidden);

    toggle.addEventListener('click', () => {
      visitorPaused = !visitorPaused;
      syncPlayback();
    });
    media.addEventListener('load', syncPlayback);
    document.addEventListener('visibilitychange', syncPlayback);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          inView = entry.isIntersecting;
          syncPlayback();
        },
        { threshold: 0.2 },
      );
      observer.observe(root);
    } else {
      inView = true;
      syncPlayback();
    }

    if (reducedMotion) command(false);
  });
};

initializeAutoplayVideos();
document.addEventListener('shopify:section:load', (event) => initializeAutoplayVideos(event.target));

// Product media gallery — one calm stage keeps the buying controls close while
// thumbnails, arrows, and variant selection expose every product asset.
const initializeProductGalleries = (scope = document) => {
  scope.querySelectorAll('[data-product-gallery]:not([data-gallery-ready])').forEach((root) => {
    const panels = Array.from(root.querySelectorAll('[data-gallery-panel]'));
    const thumbnails = Array.from(root.querySelectorAll('[data-gallery-thumbnail]'));
    if (panels.length === 0) return;

    const thumbnailRail = root.querySelector('[data-gallery-thumbnails]');
    const previous = root.querySelector('[data-gallery-previous]');
    const next = root.querySelector('[data-gallery-next]');
    const count = root.querySelector('[data-gallery-count]');
    const status = root.querySelector('[data-gallery-status]');
    let activeIndex = Math.max(0, panels.findIndex((panel) => panel.classList.contains('is-active')));

    const pausePanelMedia = (panel) => {
      panel.querySelectorAll('video').forEach((video) => video.pause());
      panel.querySelectorAll('iframe').forEach((frame) => {
        if (!frame.contentWindow) return;
        if (frame.src.includes('youtube')) {
          frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
        } else if (frame.src.includes('vimeo')) {
          frame.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
        }
      });
    };

    const activate = (index, { focusThumbnail = false, announce = true } = {}) => {
      const normalizedIndex = (index + panels.length) % panels.length;
      activeIndex = normalizedIndex;

      panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === activeIndex;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', String(!isActive));
        if (!isActive) pausePanelMedia(panel);
      });

      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === activeIndex;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-selected', String(isActive));
        thumbnail.tabIndex = isActive ? 0 : -1;
      });

      const activeThumbnail = thumbnails[activeIndex];
      if (count) count.textContent = `${activeIndex + 1} / ${panels.length}`;
      if (announce && status) {
        const mediaLabel = activeThumbnail?.dataset.mediaLabel || 'Product media';
        status.textContent = `Showing image ${activeIndex + 1} of ${panels.length}: ${mediaLabel}`;
      }
      if (activeThumbnail) {
        activeThumbnail.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
        if (focusThumbnail) activeThumbnail.focus();
      }
    };

    root.setAttribute('data-gallery-ready', '');
    if (thumbnailRail) thumbnailRail.setAttribute('role', 'tablist');
    panels.forEach((panel, index) => {
      panel.setAttribute('role', 'tabpanel');
      if (thumbnails[index]) panel.setAttribute('aria-labelledby', thumbnails[index].id);
    });
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.setAttribute('role', 'tab');
      thumbnail.setAttribute('aria-controls', panels[index].id);
      thumbnail.addEventListener('click', () => activate(index));
      thumbnail.addEventListener('keydown', (event) => {
        let nextIndex = null;
        if (event.key === 'ArrowLeft') nextIndex = index - 1;
        if (event.key === 'ArrowRight') nextIndex = index + 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = panels.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        activate(nextIndex, { focusThumbnail: true });
      });
    });
    previous?.addEventListener('click', () => activate(activeIndex - 1));
    next?.addEventListener('click', () => activate(activeIndex + 1));
    root.addEventListener('gallery:select', (event) => {
      const mediaIndex = panels.findIndex((panel) => panel.dataset.mediaId === String(event.detail?.mediaId));
      if (mediaIndex >= 0) activate(mediaIndex);
    });

    activate(activeIndex, { announce: false });
  });
};

initializeProductGalleries();
document.addEventListener('shopify:section:load', (event) => initializeProductGalleries(event.target));

// Plant matcher — keeps the merchant-configured outcomes in Liquid while the
// interaction remains reusable and survives section reloads in the editor.
const initializeQuizzes = (scope = document) => {
  scope.querySelectorAll('[data-quiz]:not([data-quiz-ready])').forEach((root) => {
    const outcomesNode = root.querySelector('[data-quiz-outcomes]');
    if (!outcomesNode) return;

    let outcomes;
    try {
      outcomes = JSON.parse(outcomesNode.textContent);
    } catch {
      return;
    }

    root.setAttribute('data-quiz-ready', '');
    const answers = {};
    const steps = Array.from(root.querySelectorAll('[data-quiz-step]'));
    const result = root.querySelector('[data-quiz-result]');
    const progress = root.querySelector('[data-quiz-progress]');
    const status = root.querySelector('[data-quiz-status]');
    let currentStep = 1;

    const animateIn = (element) => {
      if (reducedMotion) return;
      element.classList.remove('is-entering');
      element.querySelectorAll('[data-answer]').forEach((option, index) => {
        option.style.setProperty('--stagger', `${index * 55}ms`);
      });
      requestAnimationFrame(() => {
        element.classList.add('is-entering');
        window.setTimeout(() => element.classList.remove('is-entering'), 620);
      });
    };

    const updateProgress = (step, complete = false) => {
      progress.setAttribute('aria-valuenow', String(step));
      progress.setAttribute('aria-valuetext', complete ? 'Quiz complete' : `Question ${step} of 3`);
      root.querySelectorAll('[data-progress]').forEach((bar) => {
        const barStep = Number(bar.dataset.progress);
        bar.classList.toggle('is-done', complete || barStep < step);
        bar.classList.toggle('is-current', !complete && barStep === step);
      });
    };

    const showStep = (step, moveFocus = true) => {
      currentStep = step;
      result.hidden = true;
      steps.forEach((candidate) => {
        const isCurrent = Number(candidate.dataset.quizStep) === step;
        candidate.hidden = !isCurrent;
        candidate.classList.toggle('is-current', isCurrent);
        if (isCurrent) {
          candidate.tabIndex = -1;
          animateIn(candidate);
          if (moveFocus) candidate.focus({ preventScroll: true });
        }
      });
      status.textContent = `Question ${step} of 3`;
      updateProgress(step);
    };

    const panels = Array.from(root.querySelectorAll('[data-outcome-panel]'));

    const finish = () => {
      const key =
        answers.pets === 'yes'
          ? 'pets'
          : answers.light === 'low'
            ? 'low'
            : answers.effort === 'easy'
              ? 'easy'
              : 'statement';
      const outcome = outcomes[key];
      if (!outcome) return;

      steps.forEach((step) => {
        step.hidden = true;
        step.classList.remove('is-current');
      });
      root.querySelector('[data-result-title]').textContent = outcome.title;
      root.querySelector('[data-result-text]').textContent = outcome.text;

      // Every outcome's products are already in the HTML; only the match is shown.
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.outcomePanel !== key;
      });

      result.hidden = false;
      animateIn(result);
      status.textContent = 'Your plant match is ready';
      root.classList.add('is-complete');
      updateProgress(3, true);
      result.focus({ preventScroll: true });
    };

    // Restore the chosen answer when stepping back, so the quiz remembers you.
    const syncSelection = (step) => {
      const name = step.querySelector('[data-answer]')?.dataset.answer;
      step.querySelectorAll('[data-answer]').forEach((option) => {
        option.setAttribute('aria-pressed', String(answers[name] === option.dataset.value));
      });
    };

    root.querySelectorAll('[data-answer]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = button.closest('[data-quiz-step]');
        step.querySelectorAll('[data-answer]').forEach((option) => option.setAttribute('aria-pressed', 'false'));
        button.setAttribute('aria-pressed', 'true');
        answers[button.dataset.answer] = button.dataset.value;
        const stepNumber = Number(step.dataset.quizStep);

        // Hold the confirmed state briefly so the choice registers visually
        // before the step swaps underneath the pointer.
        const advance = () => (stepNumber < 3 ? showStep(stepNumber + 1) : finish());
        if (reducedMotion) advance();
        else {
          button.classList.add('is-confirmed');
          window.setTimeout(() => {
            button.classList.remove('is-confirmed');
            advance();
          }, 260);
        }
      });

      // Arrow keys move between the options of the current question.
      button.addEventListener('keydown', (event) => {
        if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;
        const group = Array.from(button.closest('[data-quiz-step]').querySelectorAll('[data-answer]'));
        const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        const next = group[(group.indexOf(button) + delta + group.length) % group.length];
        event.preventDefault();
        next.focus();
      });
    });

    root.querySelectorAll('[data-quiz-back]').forEach((button) => {
      button.addEventListener('click', () => {
        const target = Math.max(1, currentStep - 1);
        showStep(target);
        const step = steps.find((s) => Number(s.dataset.quizStep) === target);
        if (step) syncSelection(step);
      });
    });

    root.querySelector('[data-quiz-restart]').addEventListener('click', () => {
      Object.keys(answers).forEach((key) => delete answers[key]);
      root.querySelectorAll('[data-answer]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
      panels.forEach((panel) => { panel.hidden = true; });
      root.classList.remove('is-complete');
      showStep(1);
    });
  });
};

initializeQuizzes();
document.addEventListener('shopify:section:load', (event) => initializeQuizzes(event.target));

// AJAX add-to-cart confirmation. Forms still submit normally without JS.
const toast = document.querySelector('[data-cart-toast]');
let toastTimer;
const showCartToast = (title, message, isError = false) => {
  if (!toast) return;
  window.clearTimeout(toastTimer);
  toast.querySelector('[data-cart-toast-title]').textContent = title;
  toast.querySelector('[data-cart-toast-message]').textContent = message;
  toast.classList.toggle('is-error', isError);
  toast.classList.add('is-visible');
  toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), isError ? 6000 : 4200);
};

const updateCartCount = (count) => {
  const badge = document.querySelector('[data-cart-count]');
  const link = document.querySelector('[data-cart-link]');
  if (badge) {
    badge.textContent = String(count);
    badge.hidden = count === 0;
    if (!reducedMotion && count > 0) {
      badge.classList.remove('did-pop');
      requestAnimationFrame(() => badge.classList.add('did-pop'));
    }
  }
  if (link) link.setAttribute('aria-label', `Cart${count > 0 ? `, ${count} items` : ''}`);
};

const initializeCartForms = (scope = document) => {
scope.querySelectorAll('[data-cart-add-form]:not([data-cart-ready])').forEach((form) => {
  form.setAttribute('data-cart-ready', '');
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const buttons = Array.from(document.querySelectorAll('[data-cart-submit]')).filter((button) => button.form === form);
    buttons.forEach((button) => {
      button.dataset.defaultLabel = button.textContent.trim();
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';
    });

    try {
      const response = await fetch(`${window.themeRoutes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      let item = null;
      try {
        item = await response.json();
      } catch {
        // A generic recovery message is clearer than exposing a parsing error.
      }
      if (!response.ok) throw new Error(item?.description || item?.message || 'This plant could not be added. Please try again.');
      if (!item) throw new Error('The cart did not respond. Please try again.');

      let cart = null;
      try {
        const cartResponse = await fetch(`${window.themeRoutes.cart}.js`, { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) {
          cart = await cartResponse.json();
          updateCartCount(cart.item_count);
        }
      } catch {
        // The item is already in the cart; a stale badge is better than a
        // misleading failure message. The next navigation refreshes it.
      }

      const title = item.product_title || form.dataset.productTitle || 'Your plant';
      buttons.forEach((button) => {
        button.textContent = 'Added to cart';
      });
      showCartToast('Added to your cart', `${title} is ready in your cart.`);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, item } }));
    } catch (error) {
      showCartToast('Couldn’t add that plant', error.message || 'Please try again.', true);
    } finally {
      window.setTimeout(() => {
        buttons.forEach((button) => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = button.dataset.defaultLabel;
        });
      }, 900);
    }
  });
});
};

initializeCartForms();
document.addEventListener('shopify:section:load', (event) => initializeCartForms(event.target));

// On narrow screens the compact purchase bar is a continuation aid, not a
// replacement for product context. It appears only after the full form has
// been read and scrolled past.
const stickyCart = document.querySelector('[data-sticky-atc]');
const productForm = document.getElementById('ProductForm');
if (stickyCart && productForm) {
  const narrowViewport = window.matchMedia('(max-width: 900px)');
  let stickyFrame = null;
  const updateStickyCart = () => {
    stickyFrame = null;
    const show = narrowViewport.matches && productForm.getBoundingClientRect().bottom < 0;
    stickyCart.classList.toggle('is-visible', show);
    stickyCart.setAttribute('aria-hidden', String(!show));
  };
  const requestStickyUpdate = () => {
    if (stickyFrame !== null) return;
    stickyFrame = window.requestAnimationFrame(updateStickyCart);
  };
  updateStickyCart();
  window.addEventListener('scroll', requestStickyUpdate, { passive: true });
  narrowViewport.addEventListener?.('change', updateStickyCart);
}

// Keep visible prices aligned with the selected product variant.
const variantSelect = document.getElementById('Variants');
if (variantSelect) {
  variantSelect.addEventListener('change', () => {
    const selectedVariant = variantSelect.selectedOptions[0];
    const price = selectedVariant?.dataset.price;
    const mainPrice = document.getElementById('ProductPrice');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    if (price && mainPrice) mainPrice.textContent = price;
    if (price && stickyPrice) stickyPrice.textContent = price;

    const mediaId = selectedVariant?.dataset.mediaId;
    const gallery = document.querySelector('[data-product-gallery]');
    if (mediaId && gallery) gallery.dispatchEvent(new CustomEvent('gallery:select', { detail: { mediaId } }));
  });
}

// Catalog refinement uses Shopify's native filter URLs and Section Rendering
// API. Links and forms remain complete without JavaScript; with it, only the
// result shell is replaced and focus/URL state are preserved.
const initializeCatalogs = (scope = document) => {
  scope.querySelectorAll('[data-catalog-section]:not([data-catalog-ready])').forEach((catalog) => {
    catalog.setAttribute('data-catalog-ready', '');
    const sectionId = catalog.dataset.catalogSection;
    const drawer = catalog.querySelector('[data-filter-drawer]');
    const drawerPanel = drawer?.querySelector('.filter-drawer__panel');
    const openButton = catalog.querySelector('[data-filter-open]');
    const closeButtons = catalog.querySelectorAll('[data-filter-close]');
    const status = catalog.querySelector('[data-catalog-status]');
    let lastFocus = null;

    const setDrawer = (open) => {
      if (!drawer) return;
      drawer.hidden = !open;
      document.body.classList.toggle('filter-drawer-open', open);
      openButton?.setAttribute('aria-expanded', String(open));
      if (open) {
        lastFocus = document.activeElement;
        drawerPanel?.querySelector('[data-filter-close], button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])')?.focus();
      } else if (lastFocus instanceof HTMLElement) lastFocus.focus();
    };

    openButton?.addEventListener('click', () => setDrawer(true));
    closeButtons.forEach((button) => button.addEventListener('click', () => setDrawer(false)));
    drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setDrawer(false);
      if (event.key !== 'Tab' || !drawerPanel) return;
      const focusable = Array.from(
        drawerPanel.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      ).filter((element) => element.getClientRects().length > 0);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const updateCatalog = async (url, announce = 'Plant results updated') => {
      catalog.classList.add('is-loading');
      status?.setAttribute('aria-busy', 'true');
      try {
        const fetchUrl = new URL(url, window.location.origin);
        fetchUrl.searchParams.set('section_id', sectionId);
        const response = await fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('Catalog request failed');
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextCatalog = parsed.querySelector('[data-catalog-section]');
        if (!nextCatalog) throw new Error('Catalog section missing');
        catalog.replaceWith(nextCatalog);
        window.history.pushState({}, '', url);
        initializeCatalogs(document);
        initializeCartForms(nextCatalog);
        nextCatalog.querySelector('[data-catalog-status]')?.setAttribute('aria-busy', 'false');
        const nextStatus = nextCatalog.querySelector('[data-catalog-status]');
        if (nextStatus) nextStatus.textContent = announce;
        nextCatalog.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      } catch {
        window.location.assign(url);
      }
    };

    catalog.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-filter-form]');
      if (!form) return;
      event.preventDefault();
      const url = new URL(form.action, window.location.origin);
      const data = new FormData(form);
      data.forEach((value, key) => {
        if (String(value).trim()) url.searchParams.append(key, value);
      });
      setDrawer(false);
      updateCatalog(url.toString());
    });

    catalog.addEventListener('click', (event) => {
      const link = event.target.closest('[data-filter-link], .pagination a');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      setDrawer(false);
      updateCatalog(link.href);
    });

    catalog.querySelectorAll('[data-sort-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        updateCatalog(url.toString(), `Sorted by ${select.selectedOptions[0].textContent.trim()}`);
      });
    });
  });
};

initializeCatalogs();
window.addEventListener('popstate', () => window.location.reload());
document.addEventListener('shopify:section:load', (event) => initializeCatalogs(event.target));

// Care articles receive a compact table of contents from their real headings;
// authors do not need to maintain anchor links by hand.
const initializeArticleToc = (scope = document) => {
  scope.querySelectorAll('[data-article-body]:not([data-toc-ready])').forEach((body) => {
    const toc = body.closest('[data-article-layout]')?.querySelector('[data-article-toc]');
    if (!toc) return;
    const headings = Array.from(body.querySelectorAll('h2')).filter((heading) => heading.textContent.trim());
    body.setAttribute('data-toc-ready', '');
    if (headings.length < 2) {
      toc.hidden = true;
      return;
    }
    toc.innerHTML = headings.map((heading, index) => {
      if (!heading.id) heading.id = `care-step-${index + 1}`;
      return `<li><a href="#${heading.id}">${heading.textContent.trim()}</a></li>`;
    }).join('');
  });
};

initializeArticleToc();

// Related plants are requested from Shopify so merchandising stays dynamic.
const initializeRecommendations = (scope = document) => {
  scope.querySelectorAll('[data-product-recommendations]:not([data-recommendations-ready])').forEach(async (root) => {
    root.setAttribute('data-recommendations-ready', '');
    try {
      const response = await fetch(root.dataset.url);
      if (!response.ok) return;
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('[data-product-recommendations]');
      if (replacement?.innerHTML.trim()) root.replaceWith(replacement);
    } catch {
      // Recommendations are enhancement-only; the product page stays complete.
    }
  });
};

initializeRecommendations();
document.addEventListener('shopify:section:load', (event) => {
  initializeArticleToc(event.target);
  initializeRecommendations(event.target);
});

// Help-centre deep links: a question linked by id opens rather than sitting
// collapsed under the anchor.
const openTargetedDetails = () => {
  const id = window.location.hash.slice(1);
  if (!id) return;
  const target = document.getElementById(id);
  if (target instanceof HTMLDetailsElement) {
    target.open = true;
    target.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
  }
};
openTargetedDetails();
window.addEventListener('hashchange', openTargetedDetails);
